import * as React from 'react'
import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import * as models from '@owl-nest/models'
import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'

import { StepBlock } from './StepBlock'
import { ResponsiveMenu } from './ResponsiveMenu'
import { ExtraTimeBlock } from './ExtraTimeBlock'

const LABEL_MARGIN = 16
const LIST_ITEM_HEIGHT = 34
export const CLOSED_MENU_HEIGHT = 53

export type AdminSectionConfiguration = {
  projectId: number
}

export type CoachingSectionConfiguration = {
  coachAvatarSrc?: string
  discussionUrl: string
  isActive: boolean
  unreadCount?: number
}

export type MidZoneConfigurationItem = {
  hasPlainColorIcon?: boolean
  href?: string
  icon?: React.ReactNode
  isActive?: boolean
  isClickable?: boolean
  isDisabled?: boolean
  items?: {
    href?: string
    isActive?: boolean
    isDisabled?: boolean
    items?: {
      href?: string
      isActive?: boolean
      isDisabled?: boolean
      label: string
      onClick?: (event: React.MouseEvent<HTMLElement>) => void
      tooltip?: string
    }[]
    label: string
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
    tooltip?: string
  }[]
  label: string
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  subtitle?: React.ReactNode
  tooltip?: string
  withArrow?: boolean
}

export type MidZoneConfiguration = MidZoneConfigurationItem[][]

export type NavMenuProps = {
  adminSection: AdminSectionConfiguration | undefined
  className?: string
  coachingSection: CoachingSectionConfiguration | undefined
  locale: api.Lang
  midZone: MidZoneConfiguration
  project: api.Project
  mobileBreakpoint?: string
}

// TODO: A first effort of refactoring was done with the extraction of
//  `frontend/owl-nest/features/backer-backoffice/src/components/Menu/ResponsiveMenu.tsx`
//  The `ResponsiveMenu` component handles the "transformation" of the menu in an
//  animated dropdown in mobile. Since the `backer-backoffice` feature needed the
//  same behavior, the component was extracted. But to limit the impact on the
//  existing `NavMenu` its code was not changed to use `ResponsiveMenu`. If you
//  change anything in `NavMenu`, remember to port the modification in
//  `ResponsiveMenu`
export function NavMenu({
  adminSection,
  className,
  coachingSection,
  locale,
  midZone,
  project,
  mobileBreakpoint,
}: NavMenuProps): React.ReactElement {
  const shouldDisplayStepBlock =
    coachingSection &&
    ![api.ProjectStatus.ONLINE, api.ProjectStatus.WAITING, api.ProjectStatus.REFUSED].includes(project.status)

  const activeItemId = React.useMemo(() => {
    let activeItemId = ''
    for (const [i, list] of midZone.entries()) {
      for (const [j, entry] of list.entries()) {
        if (entry.isActive) {
          activeItemId = `${i}:${j}`
        }
        for (const [k, item] of (entry.items ?? []).entries()) {
          if (item.items) {
            const l = item.items.findIndex((subItem) => subItem.isActive)
            if (l > -1) {
              activeItemId = `${i}:${j}:${k}:${l}`
            }
          }
          if (item.isActive) {
            activeItemId = `${i}:${j}:${k}`
          }
        }
      }
    }
    if (coachingSection?.isActive) {
      return 'coaching'
    }
    return activeItemId
  }, [midZone])

  const elementMeasuresRef = React.useRef<{ top: number; id: string; isActive?: boolean }[]>([])

  plume.hooks.useIsoLayoutEffect(() => {
    return () => {
      elementMeasuresRef.current = []
    }
  })

  const breakpoint = mobileBreakpoint ?? plume.BREAKPOINTS.TABLET
  const projectIsDonationBased = models.project.isDonationBased(project)
  const projectIsOnline = models.project.isOnline(project)
  const projectIsVendorBased = models.project.isVendorBased(project)
  const isProjectRewardBased = models.project.isRewardBased(project)

  const openSubMenuIndices = React.useMemo(() => getOpenSubMenuIndex(midZone, projectIsOnline), [])
  const [openSubSectionIndex, setOpenSubSectionIndex] = React.useState<[number, number, number] | undefined>(
    openSubMenuIndices,
  )

  const setActiveTopPositionRef = React.useRef<(top: number) => void>()
  plume.hooks.useIsoLayoutEffect(() => {
    setTopPosition()
  }, [activeItemId, coachingSection])

  const helpURL =
    [api.ProjectStatus.NEW, api.ProjectStatus.REFUSED].includes(project.status) || !coachingSection
      ? 'https://support.ulule.com/hc/requests/new'
      : coachingSection.discussionUrl

  return (
    <>
      <ResponsiveMenu
        mobileBreakpoint={breakpoint}
        className={className}
        labelMargin={LABEL_MARGIN}
        closedMenuHeight={CLOSED_MENU_HEIGHT}
      >
        {({ setActiveTopPosition, isMobileNavOpen, containerRef }) => {
          plume.hooks.useIsoLayoutEffect(() => {
            setActiveTopPositionRef.current = setActiveTopPosition
          }, [])

          const previousMobileNavState = hooks.usePrevious<boolean>(isMobileNavOpen)
          React.useEffect(() => {
            if (!isMobileNavOpen && previousMobileNavState === true) {
              setOpenSubSectionIndex(getOpenSubMenuIndex(midZone, projectIsOnline))
              // NOTE: We want to slightly delay the computing of the top offset, to take closing menus animation into account.
              new Promise((resolve) => setTimeout(resolve, 1)).then(() => {
                setTopPosition()
              })
            }
          }, [isMobileNavOpen, previousMobileNavState])

          return (
            <Wrapper
              $breakpoint={breakpoint}
              $openMobile={isMobileNavOpen}
              ref={containerRef as React.RefObject<HTMLDivElement>}
            >
              <TopSection $hideBottomBorder={shouldDisplayStepBlock}>
                {!projectIsVendorBased && coachingSection && (
                  <Coach>
                    <CoachItemAction
                      ref={getRefMeasurer(`coaching`, coachingSection.isActive, 5)}
                      $active={coachingSection.isActive}
                      href={coachingSection.discussionUrl}
                    >
                      {coachingSection.coachAvatarSrc && (
                        <plume.styles.image.Avatar type="small" src={coachingSection.coachAvatarSrc} />
                      )}
                      <SectionTitle $breakpoint={breakpoint}>{t('Coaching')}</SectionTitle>
                      {coachingSection.unreadCount !== undefined && coachingSection.unreadCount > 0 && (
                        <plume.Jewel number={coachingSection.unreadCount} />
                      )}
                    </CoachItemAction>
                    {adminSection && (
                      <AdminItemAction
                        $breakpoint={breakpoint}
                        href={`/admin/ulule/presale/${adminSection.projectId}/change/`}
                        $isMobileNavOpen={isMobileNavOpen}
                      >
                        <span role="img" aria-label="admin">
                          😎
                        </span>
                      </AdminItemAction>
                    )}
                  </Coach>
                )}
                {isProjectRewardBased && <ExtraTimeBlock project={project} />}
              </TopSection>

              {
                // Note: coachingSection is there only for coach or staff, so we use it to hide or display this section too
                !projectIsVendorBased && shouldDisplayStepBlock && (
                  <StepBlockSection>
                    <StepBlock project={project} />
                  </StepBlockSection>
                )
              }
              <MainSection>
                <NavList>
                  {midZone.map((section, sectionIndex) => {
                    const currentSection = midZone[sectionIndex][0]

                    return (
                      <NavSection
                        $isOpen={openSubSectionIndex?.[0] === sectionIndex && currentSection.items !== undefined}
                        key={sectionIndex}
                      >
                        {section.map(
                          (
                            {
                              hasPlainColorIcon,
                              href,
                              icon,
                              isActive,
                              isClickable,
                              isDisabled,
                              items = [],
                              label,
                              onClick,
                              tooltip,
                              subtitle,
                              withArrow,
                            },
                            subSectionIndex,
                          ) => {
                            const isSectionOpen = openSubSectionIndex?.[0] === sectionIndex
                            const openTertiaryIndex = openSubSectionIndex?.[2] ?? -1
                            const isTertiarySectionOpen = isSectionOpen && openTertiaryIndex > -1
                            const tertiaryItemsLength = isTertiarySectionOpen
                              ? (items[openTertiaryIndex].items?.length ?? 0)
                              : 0

                            return (
                              <NavSubSection
                                $active={isActive}
                                $disabled={isDisabled}
                                $hasIcon={Boolean(icon)}
                                $hasSiblings={section.length > 1}
                                $isMobileNavOpen={isMobileNavOpen}
                                key={subSectionIndex}
                                $breakpoint={breakpoint}
                              >
                                <Label
                                  as={!isClickable && items.length > 0 ? 'div' : undefined}
                                  ref={getRefMeasurer(`${sectionIndex}:${subSectionIndex}`, isActive, 16)}
                                  href={href}
                                  $narrow={isClickable}
                                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                                    if (!isClickable) {
                                      toggleMenu(event)
                                    }
                                  }}
                                  uppercase
                                  $breakpoint={breakpoint}
                                >
                                  {icon && <IconWrapper $plainBackground={hasPlainColorIcon}>{icon}</IconWrapper>}
                                  {subtitle ? (
                                    <TitleWrapper>
                                      <SectionTitle as={plume.styles.heading.XXXXXS} $hasSubtitle={true}>
                                        {label}
                                      </SectionTitle>
                                      {subtitle}
                                    </TitleWrapper>
                                  ) : (
                                    <SectionTitle $breakpoint={breakpoint}>{label}</SectionTitle>
                                  )}
                                  {withArrow && <plume.glyphs.stroke.ArrowRight size={14} />}
                                  {tooltip && (
                                    <plume.Tooltip open={false} position="bottom">
                                      <plume.styles.copy.Base>{tooltip}</plume.styles.copy.Base>
                                    </plume.Tooltip>
                                  )}
                                </Label>
                                {items.length > 0 && (
                                  <span onClick={toggleMenu}>
                                    <Caret $flipped={isSectionOpen} size={15} />
                                  </span>
                                )}
                                {items.length > 0 && (
                                  <ListDrawer
                                    style={{
                                      height: isSectionOpen
                                        ? `${
                                            LIST_ITEM_HEIGHT * items.length + LIST_ITEM_HEIGHT * tertiaryItemsLength
                                          }px`
                                        : 0,
                                      marginTop: subtitle ? '-14px' : 0,
                                    }}
                                  >
                                    <SubLabelList>
                                      {items.map((item, itemIndex) => {
                                        if (item.items && item.items.length > 0) {
                                          return (
                                            <NavSubSection
                                              $active={false}
                                              $disabled={item.isDisabled}
                                              $hasSiblings={section.length > 1}
                                              $isMobileNavOpen={isMobileNavOpen}
                                              key={subSectionIndex}
                                              $breakpoint={breakpoint}
                                            >
                                              <Label
                                                as="div"
                                                ref={getRefMeasurer(
                                                  `${sectionIndex}:${subSectionIndex}:${itemIndex}`,
                                                  item.isActive,
                                                  16,
                                                )}
                                                onClick={(event: React.MouseEvent<HTMLElement>) =>
                                                  toggleMenu(event, itemIndex)
                                                }
                                                uppercase
                                                $breakpoint={breakpoint}
                                              >
                                                <SectionTitle $breakpoint={breakpoint}>{item.label}</SectionTitle>
                                                {!isDisabled && (
                                                  <Caret $flipped={openTertiaryIndex === itemIndex} size={15} />
                                                )}
                                                {item.tooltip && (
                                                  <plume.Tooltip open={false} position="bottom">
                                                    <plume.styles.copy.Base>{item.tooltip}</plume.styles.copy.Base>
                                                  </plume.Tooltip>
                                                )}
                                              </Label>
                                              <ListDrawer
                                                style={{
                                                  height:
                                                    openSubSectionIndex?.[2] === itemIndex
                                                      ? `${LIST_ITEM_HEIGHT * item.items.length}px`
                                                      : 0,
                                                }}
                                              >
                                                <SubLabelList>
                                                  {item.items.map((subItem, subItemIndex) => {
                                                    return (
                                                      <SubLabelItem
                                                        key={`nav-menu-link-${itemIndex}-${subItemIndex}`}
                                                        $disabled={subItem.isDisabled}
                                                      >
                                                        <SubLabel
                                                          ref={getRefMeasurer(
                                                            `${sectionIndex}:${subSectionIndex}:${itemIndex}:${subItemIndex}`,
                                                            subItem.isActive,
                                                          )}
                                                          $active={subItem.isActive}
                                                          disabled={subItem.isDisabled}
                                                          onClick={subItem.onClick}
                                                          href={subItem.href}
                                                          uppercase
                                                          $breakpoint={breakpoint}
                                                        >
                                                          <plume.styles.heading.XXXXXS>
                                                            {subItem.label}
                                                          </plume.styles.heading.XXXXXS>
                                                        </SubLabel>
                                                        <SubLabelTooltipWrapper>
                                                          {subItem.tooltip && subItem.isDisabled && (
                                                            <plume.Tooltip open={false} position="bottom">
                                                              <plume.styles.copy.Base>
                                                                {subItem.tooltip}
                                                              </plume.styles.copy.Base>
                                                            </plume.Tooltip>
                                                          )}
                                                        </SubLabelTooltipWrapper>
                                                      </SubLabelItem>
                                                    )
                                                  })}
                                                </SubLabelList>
                                              </ListDrawer>
                                            </NavSubSection>
                                          )
                                        }
                                        return (
                                          <SubLabelItem key={`nav-menu-link-${itemIndex}`} $disabled={item.isDisabled}>
                                            <SubLabel
                                              ref={getRefMeasurer(
                                                `${sectionIndex}:${subSectionIndex}:${itemIndex}`,
                                                item.isActive,
                                              )}
                                              $active={item.isActive}
                                              disabled={item.isDisabled}
                                              onClick={item.onClick}
                                              href={item.href}
                                              uppercase
                                              $breakpoint={breakpoint}
                                            >
                                              <plume.styles.heading.XXXXXS>{item.label}</plume.styles.heading.XXXXXS>
                                            </SubLabel>
                                            <SubLabelTooltipWrapper>
                                              {item.tooltip && item.isDisabled && (
                                                <plume.Tooltip open={false} position="bottom">
                                                  <plume.styles.copy.Base>{item.tooltip}</plume.styles.copy.Base>
                                                </plume.Tooltip>
                                              )}
                                            </SubLabelTooltipWrapper>
                                          </SubLabelItem>
                                        )
                                      })}
                                    </SubLabelList>
                                  </ListDrawer>
                                )}
                              </NavSubSection>
                            )

                            function toggleMenu(event: React.MouseEvent<HTMLElement>, tertiarySectionIndex?: number) {
                              if (!isDisabled) {
                                if (items.length > 0) {
                                  // If there is an active 3rd level submenu entry, close its parent only while keeping the 1st level menu open. Otherwise, open up the parent (2nd level).
                                  if (tertiarySectionIndex) {
                                    if (openSubSectionIndex?.[2] === tertiarySectionIndex) {
                                      setOpenSubSectionIndex([sectionIndex, subSectionIndex, -1])
                                    } else {
                                      setOpenSubSectionIndex([sectionIndex, subSectionIndex, tertiarySectionIndex])
                                    }
                                  } else if (openSubSectionIndex?.[0] === sectionIndex) {
                                    setOpenSubSectionIndex(undefined)
                                  } else {
                                    setOpenSubSectionIndex([sectionIndex, subSectionIndex, -1])
                                  }
                                }
                                if (onClick) {
                                  onClick(event)
                                }
                              }
                            }
                          },
                        )}
                      </NavSection>
                    )
                  })}
                </NavList>
              </MainSection>
              {!projectIsVendorBased && (
                <Section>
                  <HelpAndResources
                    helpURL={helpURL}
                    isMobileNavOpen={isMobileNavOpen}
                    locale={locale}
                    projectIsDonationBased={projectIsDonationBased}
                    projectIsOnline={projectIsOnline}
                    breakpoint={breakpoint}
                  />
                </Section>
              )}
            </Wrapper>
          )
        }}
      </ResponsiveMenu>
      <MobileHeightDecoy $breakpoint={breakpoint} />
    </>
  )

  function getOpenSubMenuIndex(
    midZone: MidZoneConfiguration,
    isProjectOnline: boolean,
  ): [number, number, number] | undefined {
    let sectionIndex = -1
    let subSectionIndex = -1
    let tertiarySectionIndex = -1

    if (!isProjectOnline) {
      // NOTE: When not online, we force open the first section
      sectionIndex = 0
      subSectionIndex = 0
      tertiarySectionIndex = findTertiarySectionIndex(midZone[0][sectionIndex])
    }

    if (isProjectOnline) {
      midZone.some((sections, i) => {
        subSectionIndex = sections.findIndex((section) => {
          if (section.isActive) {
            sectionIndex = i
          }
          return section.items?.some((item) => item.isActive || item.items?.some((subItem) => subItem.isActive))
        })

        if (subSectionIndex > -1) {
          sectionIndex = i
          tertiarySectionIndex = findTertiarySectionIndex(sections[sectionIndex])
        }

        return subSectionIndex > -1
      })
    }

    // NOTE: We only want to pre-open the main project nav section when project owner is cooking.
    //  As soon as the project is online, the section shall be closed by default.
    if (isProjectOnline && sectionIndex === 0 && subSectionIndex === -1) {
      return undefined
    }

    return sectionIndex > -1 || subSectionIndex > -1 ? [sectionIndex, subSectionIndex, tertiarySectionIndex] : undefined

    function findTertiarySectionIndex(subSection?: MidZoneConfigurationItem): number {
      return (
        subSection?.items?.findIndex((item) =>
          item.items?.some((tertiarySectionItem) => tertiarySectionItem.isActive),
        ) ?? -1
      )
    }
  }

  function getRefMeasurer(id: string, isActive?: boolean, margin: number = LABEL_MARGIN) {
    return (element: any) => {
      if (element instanceof HTMLElement) {
        const { top } = element.getBoundingClientRect()
        if (elementMeasuresRef.current.find((measure) => measure.id === id) === undefined) {
          elementMeasuresRef.current.push({
            top: top - margin,
            id,
            isActive,
          })
        } else {
          elementMeasuresRef.current = elementMeasuresRef.current.map((measure) =>
            measure.id === id ? { ...measure, top: top - margin, isActive } : measure,
          )
        }
      }
    }
  }

  function setTopPosition() {
    const activeElementMeasure =
      elementMeasuresRef.current.find((measure) => measure.id === activeItemId) ?? elementMeasuresRef.current[0]

    if (activeElementMeasure !== undefined && setActiveTopPositionRef.current) {
      setActiveTopPositionRef.current(activeElementMeasure.top + LABEL_MARGIN)
    }
  }
}

function HelpAndResources({
  locale,
  isMobileNavOpen,
  projectIsDonationBased,
  projectIsOnline,
  helpURL,
  breakpoint,
}: {
  helpURL: string
  isMobileNavOpen: boolean
  locale: api.Lang
  projectIsDonationBased: boolean
  projectIsOnline: boolean
  breakpoint: string
}): React.ReactElement {
  return (
    <NavSubSection $breakpoint={breakpoint} $isMobileNavOpen={isMobileNavOpen}>
      <Label $breakpoint={breakpoint} as="div" uppercase $wrapper>
        <IconWrapper>
          <TextAsIcon>?</TextAsIcon>
        </IconWrapper>
        <SectionTitle $breakpoint={breakpoint}>{t('Help and resources')}</SectionTitle>
      </Label>
      <SubLabelList>
        {locale === 'fr' && (
          <SubLabelItem>
            <SubLabel
              $breakpoint={breakpoint}
              href="https://outils.ulule.com?utm_source=ulule&utm_medium=page_projet&utm_campaign=page_projet"
              uppercase
            >
              <plume.styles.heading.XXXXXS>Boîte à outils</plume.styles.heading.XXXXXS>
            </SubLabel>
          </SubLabelItem>
        )}
        <SubLabelItem>
          <SubLabel $breakpoint={breakpoint} href="https://support.ulule.com/" uppercase>
            <plume.styles.heading.XXXXXS>{t('Support center')}</plume.styles.heading.XXXXXS>
          </SubLabel>
        </SubLabelItem>
        {locale === 'fr' && !projectIsDonationBased && (
          <SubLabelItem>
            <SubLabel
              $breakpoint={breakpoint}
              href={
                projectIsOnline
                  ? 'https://d2homsd77vx6d2.cloudfront.net/files/152/930/520/7/e2ec8f943e95f15b8e4212da24f19894.pdf'
                  : 'https://d2homsd77vx6d2.cloudfront.net/files/152/930/530/4/67c09408f283bdaa93b3333613c9f570.pdf'
              }
              uppercase
            >
              <plume.styles.heading.XXXXXS>
                {projectIsOnline ? "Guide d'animation" : t("Creator's guide")}
              </plume.styles.heading.XXXXXS>
            </SubLabel>
          </SubLabelItem>
        )}
        {locale === 'fr' && (
          <SubLabelItem>
            <SubLabel
              $breakpoint={breakpoint}
              href="https://formation.ulule.com?utm_source=ulule&utm_medium=page_projet&utm_campaign=page_projet"
              uppercase
            >
              <plume.styles.heading.XXXXXS>Formations</plume.styles.heading.XXXXXS>
            </SubLabel>
          </SubLabelItem>
        )}
        <SubLabelItem>
          <SubLabel $breakpoint={breakpoint} href={helpURL} uppercase>
            <plume.styles.heading.XXXXXS>{t('Report an issue')}</plume.styles.heading.XXXXXS>
          </SubLabel>
        </SubLabelItem>
      </SubLabelList>
    </NavSubSection>
  )
}

const MobileHeightDecoy = styled.div<{ $breakpoint: string }>`
  height: ${CLOSED_MENU_HEIGHT}px;

  @media screen and ${({ $breakpoint }) => $breakpoint} {
    display: none;
  }
`

const SubLabelList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const ListDrawer = styled.div`
  overflow: hidden;
  transition-duration: 0.3s;
  transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
`

const AdminItemAction = styled.a<{ $isMobileNavOpen: boolean; $breakpoint: string }>`
  font-size: 20px;
  position: relative;
  right: -4px;
  text-decoration: none;
  top: 5px;
  transition: right 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  ${({ $isMobileNavOpen = false }) => {
    if (!$isMobileNavOpen) {
      return css`
        right: 32px;
      `
    }
  }};

  @media screen and ${({ $breakpoint }) => $breakpoint} {
    right: -4px;
  }
`

const Caret = styled(plume.glyphs.stroke.CaretUp)<{ $flipped?: boolean }>`
  ${({ $flipped = false }) => {
    if (!$flipped) {
      return css`
        top: 2px !important;
        transform: rotate(180deg);
        transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
      `
    } else {
      return css`
        top: 3px !important;
        transform: rotate(0deg);
        transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
      `
    }
  }};
`

const CoachItemAction = styled(plume.styles.link.Primary)<{ $active: boolean }>`
  align-items: center;
  display: flex;
  position: relative;
  text-transform: uppercase;

  ${plume.styles.image.Avatar} {
    left: 0;
    margin-right: 8px;
  }

  ${({ $active = false }) => {
    if ($active) {
      return css`
        color: ${plume.COLORS.PRIMARY_BLUE_500};
      `
    }
  }}
`

const IconWrapper = styled(plume.styles.heading.M)<{ $plainBackground?: boolean }>`
  align-items: center;
  border-radius: 50%;
  border: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
  box-sizing: border-box;
  color: ${plume.COLORS.PRIMARY_SAND_500};
  display: flex;
  height: 36px;
  justify-content: center;
  left: 0;
  position: absolute;
  top: -8.5px;
  width: 36px;

  ${plume.glyphs.Icon}, svg {
    g {
      fill: currentColor;
    }
  }

  ${({ $plainBackground = false }) => {
    if ($plainBackground) {
      return css`
        border-color: ${plume.COLORS.PRIMARY_SAND_100};
        background-color: ${plume.COLORS.PRIMARY_SAND_100};
      `
    }
  }}
`

const Label = styled(plume.styles.link.Primary)<{ $narrow?: boolean; $wrapper?: boolean; $breakpoint: string }>`
  ${({ $wrapper = false }) =>
    $wrapper &&
    css`
      cursor: initial;
    `};

  ${({ $narrow = false, $breakpoint }) =>
    $narrow &&
    css`
      display: block;
      width: 90%;

      @media screen and ${$breakpoint} {
        width: 132px;
      }
    `};

  &:hover {
    ${plume.Tooltip} {
      display: flex;
    }
  }
`

const SubLabelItem = styled.li<{ $disabled?: boolean }>`
  margin: ${LABEL_MARGIN}px 0px;

  &:hover {
    ${plume.Tooltip} {
      display: flex;
    }
  }

  ${({ $disabled }) => {
    // NOTE: we're setting 'cursor: not-allowed' here because
    //  there's a 'pointer - events: none' on SubLabel (coming from plume.styles.link.Primary)
    //  already that we can't easily/properly bypass.
    if ($disabled) {
      return css`
        cursor: not-allowed;
      `
    }
  }}
`

const SubLabel = styled(plume.styles.link.Primary)<{ $active?: boolean; $breakpoint: string }>`
  background: none;
  border: none;
  display: block;
  padding: 0;
  white-space: nowrap;

  ${({ $active = false }) => {
    if ($active) {
      return css`
        color: ${plume.COLORS.PRIMARY_BLUE_500};
      `
    } else {
      return css`
        color: ${plume.COLORS.TEXT_SECONDARY};
      `
    }
  }};

  @media screen and ${({ $breakpoint }) => $breakpoint} {
    ${plume.styles.heading.XXXXXS} {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`

const SubLabelTooltipWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;

  ${plume.Tooltip} {
    &::after {
      left: 30%;
    }
  }
`

const NavSection = styled.li<{ $isOpen?: boolean }>`
  margin: 8px 0 36px;

  &:first-of-type {
    margin: 0 0 28px;
  }

  &:last-of-type {
    margin: 33px 0 0;
  }

  ${({ $isOpen = false }) => {
    if ($isOpen) {
      return css`
        margin: 8px 0 24px;
      `
    }
  }};
`

const NavSubSection = styled.div<{
  $active?: boolean
  $disabled?: boolean
  $hasIcon?: boolean
  $hasSiblings?: boolean
  $isMobileNavOpen: boolean
  $breakpoint: string
}>`
  position: relative;
  padding-left: 46px;
  transition: padding-left 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  & & {
    padding-left: 0;
  }

  ${plume.glyphs.stroke.ArrowRight} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    position: absolute;
    right: 0;
    top: 1px;
  }

  ${plume.glyphs.stroke.CaretDown}, ${plume.glyphs.stroke.CaretUp} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 0;
  }

  ${plume.Tooltip} {
    text-transform: initial;
  }

  ${({ $active = false, $isMobileNavOpen = false }) => {
    if ($active) {
      return css`
        & > ${Label} {
          color: ${plume.COLORS.PRIMARY_BLUE_500};
        }

        & ${IconWrapper} {
          background-color: ${plume.COLORS.PRIMARY_BLUE_100};
          border-color: ${plume.COLORS.PRIMARY_BLUE_100};
          color: ${plume.COLORS.PRIMARY_BLUE_500};
        }
      `
    } else if (!$active && !$isMobileNavOpen) {
      return css<{ $breakpoint: string }>`
        & ${IconWrapper} {
          display: none;

          @media screen and ${({ $breakpoint }) => $breakpoint} {
            display: flex;
          }
        }
      `
    }
  }};

  ${({ $active = false, $hasIcon = false, $isMobileNavOpen = false }) => {
    if (!$isMobileNavOpen && $hasIcon && !$active) {
      return css<{ $breakpoint: string }>`
        padding-left: 4px;

        @media screen and ${({ $breakpoint }) => $breakpoint} {
          padding-left: 46px;
        }
      `
    }
    if (!$isMobileNavOpen) {
      return css<{ $breakpoint: string }>`
        ${plume.glyphs.stroke.CaretUp} {
          display: none;
          @media screen and ${({ $breakpoint }) => $breakpoint} {
            display: block;
          }
        }
      `
    }
  }};

  ${({ $hasSiblings = false }) => {
    if ($hasSiblings) {
      return css`
        margin: 20px 0;

        &:first-of-type {
          margin: 0 0 20px;
        }

        &:last-of-type {
          margin: 20px 0 0;
        }
      `
    }
  }};

  ${({ $disabled = false }) => {
    if ($disabled) {
      return css`
        ${Label} {
          cursor: not-allowed;
          color: ${plume.COLORS.PRIMARY_GREY_500};
        }

        ${IconWrapper} {
          // NOTE: Should be BORDER_DISABLED as you would think. But that color as of time of writing (#BDBDBD) is too dark, so we're using this until it is updated.
          border-color: ${plume.COLORS.BORDER_PRIMARY};
          color: ${plume.COLORS.PRIMARY_GREY_500};
        }

        ${plume.glyphs.stroke.CaretUp} {
          color: ${plume.COLORS.PRIMARY_SAND_200};
          cursor: not-allowed;
        }
      `
    }
  }};
`

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const Section = styled.section`
  border-bottom: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
  box-sizing: border-box;
  padding: 30px 16px 26px;
  width: 100%;

  &:last-of-type {
    border-bottom: none;
  }
`

const MainSection = styled(Section)``

const TopSection = styled(Section)<{ $hideBottomBorder?: boolean }>`
  padding: 16px;

  ${(props) => {
    if (props.$hideBottomBorder) {
      return css`
        border-bottom: none;
      `
    }
  }};
`

const Coach = styled.div`
  display: flex;
  justify-content: space-between;
`

const SectionTitle = styled(plume.styles.copy.XS.semiBold)<{ $hasSubtitle?: boolean; $breakpoint: string }>`
  color: inherit;
  text-transform: uppercase;

  ${({ $hasSubtitle = false }) => {
    if ($hasSubtitle) {
      return css<{ $breakpoint: string }>`
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        text-transform: none;
        white-space: nowrap;
        width: 90%;

        @media screen and ${({ $breakpoint }) => $breakpoint} {
          width: 132px;
        }
      `
    }
  }};
`

const TextAsIcon = styled.span`
  margin-top: -4px;
`

const TitleWrapper = styled.span`
  position: relative;
  top: -10px;
`

export const Wrapper = styled.div<{ $openMobile?: boolean; $breakpoint: string }>`
  ${plume.styles.heading.XXXXXS} {
    text-transform: none;
  }

  @media screen and ${({ $breakpoint }) => $breakpoint} {
    ${(props) => {
      if (!props.$openMobile) {
        return css`
          ${IconWrapper}, ${plume.styles.image.Avatar} {
            opacity: 1;
          }
        `
      }
    }}
  }
`

const StepBlockSection = styled(Section)`
  padding-top: 0;
`
